import { Clock, MapPin, Fire, Barbell, Wind, CheckCircle, PlayCircle, ImageSquare } from "@phosphor-icons/react";

const CDN = "https://static.prod-images.emergentagent.com/jobs/1c99ae62-8685-472e-a2e2-81195b861c24/images";
const ACT_IMG = {
  strength: `${CDN}/6b24571a026f815972eb9312b2e28f22dec86cb94c13952c4747cbc96d6e9190.jpeg`,
  cardio: `${CDN}/6d864215c9cd5c098a50932d12aac0c05af136f7c274eb236deafa55d677e5d5.jpeg`,
  mobility: `${CDN}/2f5353e5d9b1bbfcf9a803cccfcdc02a4c1a328f7a9c9a27856b219aaed8c639.jpeg`,
  recovery: `${CDN}/11ac7b245565c473a35269831d995a6870e744a926b9236ba9b34c101c95d144.jpeg`,
  walk: `${CDN}/cdd4d9110d28d865892bcbf078a3226bdae0c559dad60db696be8f7c7615020e.jpeg`,
  rest: `${CDN}/620d27e9589b0fd8f2bb6d3f0c4e9baf3dc0f6765e4ab21f3cd6476842195e57.jpeg`,
  mixed: `${CDN}/39c21738a9abb449ddd710f4f2e2ededb7fb6e42ba0437f26fedf3f9978330c5.jpeg`,
};

const intensityColor = { low: "#16BC9C", moderate: "#E8A54B", high: "#EF6A56" };

const normStep = (s) => (typeof s === "string" ? { text: s, detail: "", video_query: "" } : s || {});

export default function DailyPlan({ plan }) {
  if (!plan) return null;
  const img = ACT_IMG[plan.activity_type] || ACT_IMG.mixed;
  const goal = plan.goal || plan.reasoning;
  return (
    <div className="clay overflow-hidden" data-testid="daily-plan-card">
      <div className="relative h-44">
        <img src={img} alt={plan.activity_type || "Today's activity"} className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/90 via-[#0A0F1E]/25 to-transparent" />
        <div className="absolute bottom-0 left-0 p-6">
          <span className="label-eyebrow text-white/80">Today's optimal day</span>
          <h3 className="font-display text-2xl font-medium text-white mt-1">{plan.title}</h3>
        </div>
      </div>
      <div className="p-6">
        <div className="flex flex-wrap gap-2">
          <Chip icon={Clock}>{plan.duration_min} min</Chip>
          <Chip icon={Fire} color={intensityColor[plan.intensity]}>{plan.intensity}</Chip>
          <Chip icon={MapPin}>{plan.location}</Chip>
          <Chip icon={Barbell}>{plan.focus}</Chip>
        </div>

        {goal && <p className="text-[15px] mt-4 leading-snug font-medium" data-testid="plan-goal">{goal}</p>}

        <div className="mt-4 space-y-1.5">
          {(plan.steps || []).map((raw, i) => {
            const s = normStep(raw);
            return (
              <div key={i} className="flex items-center gap-3 rounded-2xl bg-secondary/60 px-3.5 py-2.5" data-testid={`plan-step-${i}`}>
                <CheckCircle size={18} weight="fill" color="#16BC9C" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{s.text}</span>
                  {s.detail && <span className="text-xs text-muted2 block">{s.detail}</span>}
                </div>
                {s.video_query ? (
                  <a
                    href={`https://www.youtube.com/results?search_query=${encodeURIComponent(s.video_query)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`plan-step-video-${i}`}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                    title={`Watch: ${s.video_query}`}
                  >
                    <PlayCircle size={16} weight="fill" /> Watch
                  </a>
                ) : (
                  <a
                    href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${s.text} how to`)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`plan-step-photos-${i}`}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-muted2 hover:text-brand hover:underline"
                    title={`See pictures: ${s.text}`}
                  >
                    <ImageSquare size={15} weight="fill" /> See how
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {plan.cooldown && (
          <div className="mt-4 flex items-center gap-2.5 rounded-2xl bg-sleep/10 px-4 py-2.5 text-sm">
            <Wind size={18} weight="fill" color="#8B7CF6" />
            <span className="text-ink">{plan.cooldown}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ icon: Icon, children, color = "#7C8AA5" }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-secondary rounded-full px-3 py-1.5 capitalize">
      <Icon size={14} weight="fill" color={color} />
      {children}
    </span>
  );
}
